// ===========================================
// ADMIN AUTH SLICE
// ===========================================

import {
  createSlice,
  createAsyncThunk,
  type PayloadAction,
} from '@reduxjs/toolkit';
import type { AdminAuthState, StoredAdminAuth } from '@/types/admin';
import { adminAuthService } from '@/services/admin';

interface LoginPayload {
  email: string;
  password: string;
}

// Initialize state from stored auth
const storedAuth = adminAuthService.getStoredAuth();

const initialState: AdminAuthState = {
  user: storedAuth?.user ?? null,
  token: storedAuth?.token ?? null,
  status: storedAuth ? 'authenticated' : 'idle',
};

/**
 * Login async thunk
 */
export const adminLogin = createAsyncThunk(
  'adminAuth/login',
  async (payload: LoginPayload) => {
    return adminAuthService.login(payload.email, payload.password);
  }
);

/**
 * Refresh session async thunk
 */
export const refreshAdminSession = createAsyncThunk(
  'adminAuth/refresh',
  async () => {
    return adminAuthService.refreshTokens();
  }
);

/**
 * Admin auth slice
 */
const adminAuthSlice = createSlice({
  name: 'adminAuth',
  initialState,
  reducers: {
    adminLogout: (state) => {
      state.user = null;
      state.token = null;
      state.status = 'idle';
      adminAuthService.logout().catch(() => { }); // Fire and forget
      adminAuthService.clearStoredAuth();
    },
    setAdminSession: (state, action: PayloadAction<StoredAdminAuth>) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.status = 'authenticated';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(adminLogin.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
      })
      .addCase(adminLogin.fulfilled, (state, action: PayloadAction<StoredAdminAuth>) => {
        state.status = 'authenticated';
        state.user = action.payload.user;
        state.token = action.payload.token;
        state.error = undefined;
      })
      .addCase(adminLogin.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.error.message || 'Login failed';
      })
      .addCase(refreshAdminSession.fulfilled, (state, action: PayloadAction<StoredAdminAuth>) => {
        state.token = action.payload.token;
        state.user = action.payload.user;
        state.status = 'authenticated';
      })
      .addCase(refreshAdminSession.rejected, (state) => {
        state.status = 'idle';
        state.user = null;
        state.token = null;
        adminAuthService.clearStoredAuth();
      });
  },
});

export const { adminLogout, setAdminSession } = adminAuthSlice.actions;
export const adminAuthReducer = adminAuthSlice.reducer;
